/**
 * Reusable bulk-edit modal.
 * Each field has a checkbox: only checked fields are applied to selected rows.
 * Built on top of the shared <Modal/> so it always sits centered with a
 * sticky footer (no more scrolling to find Apply / Cancel on tall forms).
 */
import { useState } from "react";
import Modal from "./Modal";

export default function BulkEditModal({
  title,
  fields,            // [{ name, label, type?: "text"|"number"|"checkbox"|"select", options?: string[] }]
  selectedCount,
  onClose,
  onApply,           // async (patchObj) => void
}) {
  const [enabled, setEnabled] = useState({});      // {fieldName: bool}
  const [values, setValues] = useState({});        // {fieldName: value}
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  const toggle = (name) => setEnabled((s) => ({ ...s, [name]: !s[name] }));
  const setVal = (name, v) => setValues((s) => ({ ...s, [name]: v }));

  const submit = async (e) => {
    e?.preventDefault?.();
    const patch = {};
    fields.forEach((f) => {
      if (enabled[f.name]) {
        let v = values[f.name];
        if (f.type === "number") v = v === "" || v === undefined ? null : Number(v);
        else if (f.type === "checkbox") v = !!v;
        patch[f.name] = v;
      }
    });
    if (Object.keys(patch).length === 0) {
      setErr("Tick at least one field to apply.");
      return;
    }
    setErr("");
    setSubmitting(true);
    try { await onApply(patch); }
    catch (e2) { setErr(e2?.message || String(e2)); }
    finally { setSubmitting(false); }
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      size="xl"
      footer={
        <>
          <button type="button" onClick={onClose} className="es-btn-ghost">Cancel</button>
          <button
            type="button"
            onClick={submit}
            data-testid="bulk-edit-apply"
            disabled={submitting}
            className="es-btn-primary disabled:opacity-60"
          >
            {submitting ? "Applying…" : `Apply to ${selectedCount}`}
          </button>
        </>
      }
    >
      <div className="px-6 py-5 space-y-4">
        <p className="text-xs text-zinc-500 font-mono" data-testid="bulk-edit-count">
          {selectedCount} record(s) selected · tick a field to apply
        </p>
        {fields.map((f) => (
          <div
            key={f.name}
            className={["border rounded-lg p-3 transition-colors", enabled[f.name] ? "bg-brand-50/50 border-brand-300" : "bg-zinc-50 border-zinc-200"].join(" ")}
          >
            <label className="flex items-center gap-2 mb-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={!!enabled[f.name]}
                onChange={() => toggle(f.name)}
                data-testid={`bulk-toggle-${f.name}`}
              />
              <span className="font-semibold text-sm">{f.label}</span>
            </label>
            {enabled[f.name] && (
              f.type === "select" ? (
                <select
                  data-testid={`bulk-value-${f.name}`}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  className="es-select w-full"
                >
                  <option value="">— pick —</option>
                  {f.options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid={`bulk-value-${f.name}`}
                    checked={!!values[f.name]}
                    onChange={(e) => setVal(f.name, e.target.checked)}
                  /> {f.label}
                </label>
              ) : (
                <input
                  data-testid={`bulk-value-${f.name}`}
                  type={f.type || "text"}
                  step={f.type === "number" ? "any" : undefined}
                  value={values[f.name] ?? ""}
                  onChange={(e) => setVal(f.name, e.target.value)}
                  className="es-input"
                  placeholder={f.placeholder}
                />
              )
            )}
          </div>
        ))}

        {err && <div className="border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 rounded-lg">{err}</div>}
      </div>
    </Modal>
  );
}
